import React, { useState, useEffect } from "react";
import {
  Grid,
  Paper,
  Box,
  Typography,
  TextField,
  Button,
  Avatar,
  List,
  ListItemIcon,
  ListItemText,
  ListItemButton,
  InputAdornment,
  Divider,
  Container,
  Icon,
  Alert,
  Snackbar,
} from "@mui/material";
import { useTheme, alpha } from "@mui/material/styles";
import HeaderIcons from "../../Images/headericon.png";
import PersonIcon from "@mui/icons-material/Person";
import LinkIcon from "@mui/icons-material/Link";
import GroupAddIcon from "@mui/icons-material/GroupAdd";
import AccountBalanceIcon from "@mui/icons-material/AccountBalance";
import LockIcon from "@mui/icons-material/Lock";
import DarkModeIcon from "@mui/icons-material/DarkMode";
import HelpIcon from "@mui/icons-material/Help";
import ShieldIcon from "@mui/icons-material/Shield";
import SyncIcon from "@mui/icons-material/Sync";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import VerifiedIcon from "@mui/icons-material/Verified";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import BusinessIcon from "@mui/icons-material/Business";
import EmailIcon from "@mui/icons-material/Email";
import { useSelector, useDispatch } from "react-redux";
import { GetUserInfo, UpdateUserInfo, UpdateEntityInfo } from "../../Redux/Actions/userStatusService";
import { useUser } from "../../Context/UserContext";
import SecurityDetails from "./SecurityDetails";
import TeamManagement from "./TeamManagement";

function ProfileSettingsComponent() {
  const theme = useTheme();
  const dispatch = useDispatch();
  const { userRole, userInfo } = useUser();
  const permissions = useSelector((state) => state.permissions || []);
  
  // Get the tab parameter from the URL
  const pathname = window.location.pathname;
  const tabFromUrl = pathname.split('/')[2];
  
  // Map URL parameters to menu labels
  const urlToMenuMap = {
    'account': 'Account',
    'adduser': 'Add Users',
    'privacy': 'Privacy',
    'security': 'Security',
    'appearance': 'Appearance',
    'support': 'Support'
  };
  
  // Initialize selectedMenu with the mapped URL tab parameter or default to 'Account'
  const [activeSection, setActiveSection] = useState(urlToMenuMap[tabFromUrl] || 'Account');
  
  const [personalInfo, setPersonalInfo] = useState({
    firstName: "",
    lastName: "",
    email: "",
    entityName: "",
  });

  const [userInfoData, setUserInfoData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success'
  });

  // Fetch user info on component mount
  useEffect(() => {
    const fetchUserInfo = async () => {
      try {
        setLoading(true);
        const response = await dispatch(GetUserInfo());
        
        if (response.payload?.statusCode === 200) {
          const userData = response.payload.data;
          setUserInfoData(userData);
          
          // Populate form with user data based on new API structure
          setPersonalInfo({
            firstName: userData?.userInfo?.firstName || "",
            lastName: userData?.userInfo?.lastName || "",
            email: userData?.email || "",
            entityName: userData?.entityInfo?.name || "",
          });
        } else {
          console.error("Failed to fetch user info:", response);
        }
      } catch (error) {
        console.error("Error fetching user info:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchUserInfo();
  }, [dispatch]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setPersonalInfo({
      ...personalInfo,
      [name]: value,
    });
  };

  const handleSaveChanges = async () => {
    try {
      setSaving(true);
      
      if (userRole === 'SUB_MANAGER') {
        // Update user info for SUB_MANAGER
        const userInfoPayload = {
          firstName: personalInfo.firstName,
          lastName: personalInfo.lastName,
        };
        const response = await dispatch(UpdateUserInfo(userInfoPayload));
        
        if (response.payload?.statusCode === 200) {
          // Fetch updated user info after successful save
          const updatedUserResponse = await dispatch(GetUserInfo());
          if (updatedUserResponse.payload?.statusCode === 200) {
            const updatedUserData = updatedUserResponse.payload.data;
            setUserInfoData(updatedUserData);
            setPersonalInfo({
              firstName: updatedUserData?.userInfo?.firstName || "",
              lastName: updatedUserData?.userInfo?.lastName || "",
              email: updatedUserData?.email || "",
              entityName: updatedUserData?.entityInfo?.name || "",
            });
          }
          
          setSnackbar({
            open: true,
            message: 'Personal information updated successfully!',
            severity: 'success'
          });
        } else {
          throw new Error('Failed to update user info');
        }
      } else if (userRole === 'MANAGER') {
        // Update entity info for MANAGER
        const entityInfoPayload = {
          name: personalInfo.entityName,
          entityType: "INVESTOR", // Default entity type
        };
        
        const response = await dispatch(UpdateEntityInfo(entityInfoPayload));
        
        if (response.payload?.statusCode === 200) {
          // Fetch updated user info after successful save
          const updatedUserResponse = await dispatch(GetUserInfo());
          if (updatedUserResponse.payload?.statusCode === 200) {
            const updatedUserData = updatedUserResponse.payload.data;
            setUserInfoData(updatedUserData);
            setPersonalInfo({
              firstName: updatedUserData?.userInfo?.firstName || "",
              lastName: updatedUserData?.userInfo?.lastName || "",
              email: updatedUserData?.email || "",
              entityName: updatedUserData?.entityInfo?.name || "",
            });
          }
          
          setSnackbar({
            open: true,
            message: 'Entity information updated successfully!',
            severity: 'success'
          });
        } else {
          throw new Error('Failed to update entity info');
        }
      }
    } catch (error) {
      console.error("Error saving changes:", error);
      setSnackbar({
        open: true,
        message: 'Failed to update information. Please try again.',
        severity: 'error'
      });
    } finally {
      setSaving(false);
    }
  };

  const handleCloseSnackbar = () => {
    setSnackbar({
      ...snackbar,
      open: false
    });
  };
  
  const hasPermission = (permissionName) => {
    return permissions?.some(permission => 
      permission?.apiName === permissionName && permission?.canAccess
    );
  };
  
  const handleMenuClick = (label) => {
    setActiveSection(label);
    // Update URL when menu item is clicked
    const urlParam = Object.entries(urlToMenuMap).find(([key, value]) => value === label)?.[0];
    if (urlParam) {
      window.history.pushState({}, '', `/profile-settings/${urlParam}`);
    }
  };

  // Different menu items based on user role
  const getMenuItems = () => {
    if (userRole === 'SUB_MANAGER') {
      return [
        { icon: <PersonIcon />, label: 'Account', description: 'Update personal information', permission: 'updateUserInfo' },
        { icon: <LockIcon />, label: 'Privacy', description: 'Update password' },
        { icon: <ShieldIcon />, label: 'Security', description: 'Enable two-factor authentication' },
        { icon: <DarkModeIcon />, label: 'Appearance', description: 'Light or Dark mode' },
        { icon: <HelpIcon />, label: 'Support', description: 'Have questions? Reach out' },
      ];
    } else if (userRole === 'MANAGER') {
      return [
        { icon: <PersonIcon />, label: 'Account', description: 'Update entity information', permission: 'updateUserInfo' },
        { icon: <GroupAddIcon />, label: 'Your Team', description: 'Add sub-admins and managers to your team' },
        { icon: <LockIcon />, label: 'Privacy', description: 'Update password' },
        { icon: <ShieldIcon />, label: 'Security', description: 'Enable two-factor authentication' },
        { icon: <DarkModeIcon />, label: 'Appearance', description: 'Light or Dark mode' },
        { icon: <HelpIcon />, label: 'Support', description: 'Have questions? Reach out' },
      ];
    }
    return [];
  };

  const menuItems = getMenuItems();

  const renderContent = () => {
    switch (activeSection) {
      case "Account":
        return (
          <Paper 
            elevation={1} 
            sx={{ 
              p: 3, 
              minHeight: '500px',
              backgroundColor: 'white',
              width: "80%",
              borderRadius: '12px',
              boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
            }}
          >
            <Typography 
              variant="h6" 
              sx={{ 
                mb: 3, 
                fontWeight: 600,
                fontSize: '1.25rem',
                color: '#333'
              }}
            >
              {userRole === 'SUB_MANAGER' ? 'Personal Details' : 'Entity Details'}
            </Typography>
            
            <Grid container spacing={3}>
              {userRole === 'SUB_MANAGER' ? (
                <>
                  <Grid item xs={12}>
                    <Typography 
                      variant="body2" 
                      color="text.secondary" 
                      gutterBottom
                      sx={{ 
                        fontWeight: 500,
                        fontSize: '0.875rem',
                        color: '#666',
                        mb: 1
                      }}
                    >
                      First Name
                    </Typography>
                    <TextField
                      fullWidth
                      name="firstName"
                      value={personalInfo.firstName}
                      onChange={handleInputChange}
                      variant="outlined"
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          '& fieldset': {
                            borderColor: '#e0e0e0',
                          },
                          '&:hover fieldset': {
                            borderColor: '#1976d2',
                          },
                          '&.Mui-focused fieldset': {
                            borderColor: '#1976d2',
                          },
                        },
                      }}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <Typography 
                      variant="body2" 
                      color="text.secondary" 
                      gutterBottom
                      sx={{ 
                        fontWeight: 500,
                        fontSize: '0.875rem',
                        color: '#666',
                        mb: 1
                      }}
                    >
                      Last Name
                    </Typography>
                    <TextField
                      fullWidth
                      name="lastName"
                      value={personalInfo.lastName}
                      onChange={handleInputChange}
                      variant="outlined"
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          '& fieldset': {
                            borderColor: '#e0e0e0',
                          },
                          '&:hover fieldset': {
                            borderColor: '#1976d2',
                          },
                          '&.Mui-focused fieldset': {
                            borderColor: '#1976d2',
                          },
                        },
                      }}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <Typography 
                      variant="body2" 
                      color="text.secondary" 
                      gutterBottom
                      sx={{ 
                        fontWeight: 500,
                        fontSize: '0.875rem',
                        color: '#666',
                        mb: 1
                      }}
                    >
                      Email Address
                    </Typography>
                    <TextField
                      fullWidth
                      name="email"
                      value={personalInfo.email}
                      variant="outlined"
                      disabled
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">
                            <EmailIcon sx={{ color: '#999' }} />
                          </InputAdornment>
                        ),
                      }}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          '& fieldset': {
                            borderColor: '#e0e0e0',
                          },
                          '& input': {
                            color: '#999',
                          },
                        },
                        '& .Mui-disabled': {
                          backgroundColor: '#f5f5f5',
                        },
                      }}
                    />
                  </Grid>
                </>
              ) : (
                <>
                  <Grid item xs={12}>
                    <Typography 
                      variant="body2" 
                      color="text.secondary" 
                      gutterBottom
                      sx={{ 
                        fontWeight: 500,
                        fontSize: '0.875rem',
                        color: '#666',
                        mb: 1
                      }}
                    >
                      Entity Name
                    </Typography>
                    <TextField
                      fullWidth
                      name="entityName"
                      value={personalInfo.entityName}
                      onChange={handleInputChange}
                      variant="outlined"
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">
                            <BusinessIcon sx={{ color: '#999' }} />
                          </InputAdornment>
                        ),
                      }}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          '& fieldset': {
                            borderColor: '#e0e0e0',
                          },
                          '&:hover fieldset': {
                            borderColor: '#1976d2',
                          },
                          '&.Mui-focused fieldset': {
                            borderColor: '#1976d2',
                          },
                        },
                      }}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <Typography 
                      variant="body2" 
                      color="text.secondary" 
                      gutterBottom
                      sx={{ 
                        fontWeight: 500,
                        fontSize: '0.875rem',
                        color: '#666',
                        mb: 1
                      }}
                    >
                      Email Address
                    </Typography>
                    <TextField
                      fullWidth
                      name="email"
                      value={personalInfo.email}
                      variant="outlined"
                      disabled
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">
                            <EmailIcon sx={{ color: '#999' }} />
                          </InputAdornment>
                        ),
                      }}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          '& fieldset': {
                            borderColor: '#e0e0e0',
                          },
                          '& input': {
                            color: '#999',
                          },
                        },
                        '& .Mui-disabled': {
                          backgroundColor: '#f5f5f5',
                        },
                      }}
                    />
                  </Grid>
                </>
              )}
            </Grid>
            
            <Box sx={{ display: "flex", mt: 4, gap: 2 }}>
              <Button
                variant="contained"
                onClick={handleSaveChanges}
                disabled={saving}
                sx={{
                  backgroundColor: '#1976d2',
                  color: 'white',
                  px: 3,
                  py: 1.5,
                  borderRadius: '8px',
                  textTransform: 'none',
                  fontWeight: 500,
                  fontSize: '0.875rem',
                  '&:hover': {
                    backgroundColor: '#1565c0',
                  },
                  '&:disabled': {
                    backgroundColor: '#ccc',
                    color: '#666',
                  },
                }}
              >
                {saving ? 'Saving...' : 'Save changes'}
              </Button>
              <Button
                variant="outlined"
                disabled={saving}
                sx={{
                  borderColor: '#1976d2',
                  color: '#1976d2',
                  px: 3,
                  py: 1.5,
                  borderRadius: '8px',
                  textTransform: 'none',
                  fontWeight: 500,
                  fontSize: '0.875rem',
                  '&:hover': {
                    borderColor: '#1565c0',
                    backgroundColor: 'rgba(25, 118, 210, 0.04)',
                  },
                  '&:disabled': {
                    borderColor: '#ccc',
                    color: '#ccc',
                  },
                }}
              >
                Cancel
              </Button>
            </Box>
          </Paper>
        );
      case "Your Team":
        return <TeamManagement />;
      case "Security":
        return <SecurityDetails />;
      default:
        return null;
    }
  };

  if (loading) {
    return (
      <Container maxWidth="lg">
        <Typography variant="h4" sx={{ fontWeight: 600, color: "#18275A", mt: 2 }}>
          Profile
        </Typography>
        <Typography variant="subtitle1" color="text.secondary" sx={{ mb: 4 }}>
          Loading profile information...
        </Typography>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg">
      <Typography variant="h4" sx={{ fontWeight: 600, color: "#18275A", mt: 2 }}>
        Profile
      </Typography>
      <Typography variant="subtitle1" color="text.secondary" sx={{ mb: 4 }}>
        Lorem ipsum dolor sit amet consectetur. Accumsan eget est.
      </Typography>
      
      <Box sx={{ display: 'flex', gap: 3 }}>
        <Box sx={{ width: '40%' }}>
          <Paper elevation={1} sx={{ p: 3, height: "100%" }}>
            <Box sx={{ display: "flex", alignItems: "center", mb: 3 }}>
              <Avatar
                sx={{
                  width: 64,
                  height: 64,
                  bgcolor: "#e0e0e0",
                  fontSize: "1.5rem",
                  mr: 2,
                }}
              >
                {personalInfo.firstName ? personalInfo.firstName.charAt(0) : "M"}
              </Avatar>
              <Box>
                <Typography variant="h6" sx={{ 
                  display: "flex", 
                  alignItems: "center", 
                  fontWeight: 500,
                  color: "#18275A"
                }}>
                  {userRole === 'MANAGER' ? 'Manager' : 'Sub Manager'}
                  <VerifiedIcon color="primary" sx={{ ml: 0.5, width: 20, height: 20 }} />
                </Typography>
               
              </Box>
            </Box>

            <Divider sx={{ my: 2 }} />

            <List component="nav" sx={{ width: '100%' }}>
              {menuItems.map((item) => {
                // Check if user has permission for this menu item
                const userType = localStorage.getItem('type');
                if (userType === 'SUB_INVESTOR' && item.permission && !hasPermission(item.permission)) {
                  return null;
                }
                
                return (
                  <ListItemButton 
                    key={item.label}
                    selected={activeSection === item.label}
                    onClick={() => handleMenuClick(item.label)}
                    sx={{ 
                      borderRadius: 1,
                      mb: 1,
                      '&.Mui-selected': {
                        backgroundColor: alpha(theme.palette.primary.main, 0.1),
                      },
                      '&:hover': {
                        backgroundColor: alpha(theme.palette.primary.main, 0.08),
                      }
                    }}
                  >
                    <ListItemIcon sx={{ color: 'text.secondary' }}>
                      {item.icon}
                    </ListItemIcon>
                    <ListItemText 
                      primary={item.label} 
                      secondary={item.description}
                      primaryTypographyProps={{ 
                        variant: 'subtitle1', 
                        sx: { fontWeight: 500, color: '#18275A' } 
                      }}
                      secondaryTypographyProps={{ 
                        variant: 'body2', 
                        sx: { color: 'text.secondary', fontSize: '0.75rem' } 
                      }}
                    />
                    <ChevronRightIcon sx={{ color: 'text.secondary' }} />
                  </ListItemButton>
                );
              })}
            </List>
          </Paper>
        </Box>

        <Box sx={{ width: '60%' }}>
          {renderContent()}
        </Box>
      </Box>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert 
          onClose={handleCloseSnackbar} 
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  );
}

export default ProfileSettingsComponent; 